import { t } from "@lingui/macro";
import { Check } from "@phosphor-icons/react";
import { Button } from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

type Plan = {
  name: string;
  price: string;
  period: string;
  description: string;
  badge?: string;
  highlights: string[];
  cta: string;
  href: string;
  featured?: boolean;
};

const plans = (): Plan[] => [
  {
    name: t`Free`,
    price: "$0",
    period: t`/mo`,
    description: t`No card required. Get a feel for how it works.`,
    badge: t`No card required`,
    highlights: [
      t`1 resume limit`,
      t`Limited AI keyword targeting`,
      t`All ATS-friendly templates`,
      t`3 PDF downloads`,
    ],
    cta: t`Get started free`,
    href: "/ai-generator",
  },
  {
    name: t`Pro`,
    price: "$29",
    period: t`/mo`,
    description: t`Full AI writers, unlimited resumes, and priority support.`,
    badge: t`Most popular`,
    featured: true,
    highlights: [
      t`Unlimited resumes`,
      t`Full AI writers & editors`,
      t`Unlimited PDF exports`,
      t`Job search & tracking`,
    ],
    cta: t`Start Pro trial`,
    href: "/pricing",
  },
  {
    name: t`Enterprise`,
    price: t`Custom`,
    period: "",
    description: t`For teams, universities, and workforce programs.`,
    highlights: [
      t`Bulk seats & admin dashboard`,
      t`Custom branding`,
      t`Dedicated support`,
      t`API access`,
    ],
    cta: t`Contact sales`,
    href: "/contact",
  },
];

export const PricingTeaserSection = () => {
  const items = plans();

  return (
    <section id="pricing-teaser" className="relative bg-[#050505] py-24 sm:py-32">
      {/* Background Glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none flex justify-center">
        <div className="w-full max-w-[800px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] -translate-y-1/2" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto mb-16 max-w-2xl text-center"
        >
          <div className="mb-6 flex justify-center">
            <span className="rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] backdrop-blur-md">
              {t`Pricing`}
            </span>
          </div>
          <h2 className="font-sans text-3xl font-bold tracking-tighter text-white sm:text-4xl md:text-5xl">
            {t`Choose the plan that fits your job search`}
          </h2>
          <p className="mt-6 text-lg font-light text-white/60">
            {t`Pays for itself with the time you'll save creating one resume.`}
          </p>
        </motion.div>

        <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-3">
          {items.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={cn(
                "relative flex flex-col p-2 rounded-[2.5rem] border",
                plan.featured
                  ? "border-emerald-500/30 bg-emerald-500/5 shadow-[0_0_80px_-20px_rgba(16,185,129,0.2)]"
                  : "border-white/10 bg-[#0a0a0a]",
              )}
            >
              <div className="relative flex flex-col h-full rounded-[calc(2.5rem-0.5rem)] border border-white/5 bg-[#050505] p-6 sm:p-8 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] overflow-hidden">
                {plan.featured && (
                  <div className="absolute top-0 right-0 -mr-4 -mt-4 h-24 w-24 rounded-full bg-emerald-500/20 blur-2xl" />
                )}
                
                {plan.badge && (
                  <span
                    className={cn(
                      "mb-6 inline-flex w-fit rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-wider",
                      plan.featured
                        ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300"
                        : "border-white/10 bg-white/5 text-white/60",
                    )}
                  >
                    {plan.badge}
                  </span>
                )}
                <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-sans text-5xl font-bold tracking-tighter text-white">
                    {plan.price}
                  </span>
                  {plan.period && (
                    <span className="text-sm font-medium text-white/50">{plan.period}</span>
                  )}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-white/60 font-light">
                  {plan.description}
                </p>
                <div className="my-8 h-px w-full bg-gradient-to-r from-white/0 via-white/10 to-white/0" />
                <ul className="flex-1 space-y-4">
                  {plan.highlights.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-white/80 font-light">
                      <Check weight="bold" className="mt-0.5 size-4 shrink-0 text-emerald-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className={cn(
                    "mt-10 w-full rounded-full py-6 font-bold shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]",
                    plan.featured
                      ? "bg-emerald-400 text-emerald-950 hover:bg-emerald-300"
                      : "bg-white/10 text-white hover:bg-white/20 border border-white/5",
                  )}
                >
                  <Link to={plan.href}>{plan.cta}</Link>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
